import { Injector } from '@angular/core';
import { map, random, times } from '@sama/micro-dash';
import { ShapeLocationState2D } from '../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState2D } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { AbstractToolHelper } from '../../../cross-team-shared/annotation/shared/tool-helpers/abstract-tool-helper';
import {
  InteractiveBorderEventInfo,
  SubpointEventInfo,
} from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { Dimensions } from '../../../cross-team-shared/types/misc';
import { VectorLayerEditMode } from '../../../cross-team-shared/types/tool-modes';
import { pickExisting } from '../../../shared/misc-utilities/misc-utils';
import { reducePoints } from '../../../shared/misc-utilities/point-utils';
import { PartiallyTransformedShape } from '../../../team-apis/content/content-utils';
import {
  Point2D,
  VectorTaggingShapeType,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { SingleSubpointTranslationHelper } from '../tools/edit/single-subpoint-translation-helper';
import { VectorDragHandler } from '../tools/shared/drag-handlers/vector-drag-handler';

export class ShapeHelper {
  allowsSubpointSelection = false;
  allowsAddingAndRemovingSubpoints = false;
  allowsMLExtrapolation = false;

  arePointsValid(_points: Point2D[]) {
    return true;
  }

  areSubpointsDeletable(points: Point2D[]) {
    return (
      this.allowsAddingAndRemovingSubpoints &&
      points.length > this.getMinPoints()
    );
  }

  doesSubpointMoveWithShape(_index: number) {
    return true;
  }

  getSingleSubpointDragHandler(
    eventInfo: SubpointEventInfo,
    _editMode: VectorLayerEditMode,
    injector: Injector,
  ) {
    return new VectorDragHandler(
      eventInfo.event,
      injector,
      new SingleSubpointTranslationHelper(
        eventInfo.shapeStore,
        eventInfo.subpointIndex,
        injector,
      ),
      {
        restrictInteractions: true,
      },
    );
  }

  getInteractiveBorderDragHandler(
    _eventInfo: InteractiveBorderEventInfo,
    _injector: Injector,
  ): AbstractToolHelper {
    throw new Error('Only rectangle borders can be resized by dragging!');
  }

  getInsertIndex(_oldPoints: Point2D[], _newPoint: Point2D): number {
    throw new Error('Not supported for this shape type!');
  }

  getCursorForInteractiveBorder(
    _editMode: string,
    _lineIndex: number,
  ): string | undefined {
    return undefined;
  }

  isSubpointDraggingEnabled(editMode: VectorLayerEditMode): boolean {
    return editMode !== 'clicky';
  }

  makeRandomPoints(imageSize: Dimensions, max: number) {
    const min = this.getMinPoints();
    // fix me if you change this code
    // eslint-disable-next-line no-param-reassign -- fix me!
    max = Math.max(min, max);
    return makeRandomSubpoints(imageSize, random(min, max));
  }

  getMinPoints() {
    return 1;
  }

  locationsAnswerToState(locations: ShapeLocationState2D[]) {
    return this.convertPoints(locations, this.pointsAnswerToState);
  }

  locationsStateToAnswer(locations: ShapeLocationState2D[]) {
    return this.convertPoints(locations, this.pointsStateToAnswer);
  }

  pointsStateToAnswer(points: Point2D[]) {
    return points;
  }

  pointsAnswerToState(points: Point2D[]) {
    return points;
  }

  convertShapeStateToAnswer(shapeState: ShapeState2D) {
    return {
      ...pickExisting(
        shapeState,
        'type',
        'index',
        'source',
        'dynamic_tags',
        'linkedShapeId',
      ),
      tags: shapeState.tags,
      key_locations: this.locationsStateToAnswer(shapeState.key_locations),
    };
  }

  addKeyLocationsToShapeExternal(
    shapeExternal: PartiallyTransformedShape<Point2D, VectorTaggingShapeType>,
  ) {
    return {
      ...shapeExternal,
      key_locations: this.locationsAnswerToState(shapeExternal.key_locations),
    };
  }

  getNumberOfLines(points: Point2D[]) {
    return points.length - 1;
  }

  fixPoints(points: Point2D[], _pointsForSliderLocking?: Point2D[]) {
    return points;
  }

  getBoundingPoints(points: Point2D[]): [Point2D, Point2D] {
    return [reducePoints(points, Math.min), reducePoints(points, Math.max)];
  }

  private convertPoints(
    locations: ShapeLocationState2D[],
    conversionFn: (points: Point2D[]) => Point2D[],
  ) {
    return locations.map((location) => ({
      ...location,
      points: conversionFn(location.points),
      ...(location.extrapolation && {
        extrapolation: {
          source: location.extrapolation.source,
          points: this.convertExtrapolatedPoints(location, conversionFn),
        },
      }),
    }));
  }

  private convertExtrapolatedPoints(
    location: ShapeLocationState2D,
    conversionFn: (points: Point2D[]) => Point2D[],
  ) {
    return map(location.extrapolation?.points, (points) =>
      conversionFn(points),
    );
  }
}

export function makeRandomSubpoints(imageSize: Dimensions, pointCount: number) {
  const width = 0.2 * imageSize.width;
  const height = 0.2 * imageSize.height;
  const left = random(imageSize.width - width, false);
  const top = random(imageSize.height / 2 - height, false);
  return times(
    pointCount,
    (): Point2D => [left + random(width, false), top + random(height, false)],
  );
}
